import { warn } from '@execonline-inc/logging';
import { toTask } from '@execonline-inc/maybe-adapter';
import { assertNever } from '@kofno/piper';
import { Maybe } from 'maybeasy';
import { Task } from 'taskarian';
import { AppyError, callApi } from '../Appy';
import { currentUserStore } from '../CurrentUserStore';
import ErrorActionableReaction, { EAProps, handleError } from '../ErrorActionableReaction';
import { MissingLinkError, findLinkT } from '../LinkyLinky';
import { Link } from '../Resource/Types';
import { reportResourceDecoder } from './Decoders';
import { State } from './Types';
import ReportsStore from './index';

interface Props extends EAProps<ReportsStore> {
  reportId: Maybe<number>;
}

type ReportError = MissingReportId | AppyError | MissingLinkError;

interface MissingReportId {
  kind: 'missing-report-id';
}

const processTemplateLink =
  (reportId: Maybe<number>) =>
  (link: Link): Task<MissingReportId, Link> =>
    Task.succeed<MissingReportId, Maybe<number>>(reportId)
      .andThen(toTask<MissingReportId, number>({ kind: 'missing-report-id' }))
      .map((v) => ({
        ...link,
        href: link.href.replace('{report_id}', `${v}`),
      }));

const reportLink =
  (reportId: Maybe<number>) =>
  (links: ReadonlyArray<Link>): Task<ReportError, Link> =>
    Task.succeed<ReportError, ReadonlyArray<Link>>(links)
      .andThen(findLinkT('report-template'))
      .andThen(processTemplateLink(reportId))
      .orElse(() => findLinkT('report', links));

const handleReportsError = (store: ReportsStore) => (error: ReportError) => {
  switch (error.kind) {
    case 'missing-link-error':
      store.error('Report link is unavailable');
      warn('ReportReactions', `Missing link: ${error.rel}`, error);
      break;
    case 'missing-report-id':
      store.error('Report ID is not present');
      break;
    default:
      handleError(store, error);
  }
};

const getReports = callApi(reportResourceDecoder, {});

class ReportReactions extends ErrorActionableReaction<ReportsStore, State, Props> {
  tester = () => this.props.store.state;
  effect = (state: State) => {
    const { store } = this.props;
    switch (state.kind) {
      case 'waiting':
        break;
      case 'loading':
        Task.succeed<ReportError, ReadonlyArray<Link>>(currentUserStore.links)
          .andThen(reportLink(this.props.reportId))
          .andThen(getReports)
          .fork(handleReportsError(store), store.ready);
        break;
      case 'ready':
      case 'error':
        break;
      default:
        assertNever(state);
    }
  };
}

export default ReportReactions;
